import {
  Snowflake,
  Leaf,
  Cloud,
  Brain,
  Undo2,
  Target,
  Battery,
  Trophy,
  Hourglass,
  Users,
} from "lucide-react";

// Each feature is mapped to an ADHD-safe color role:
// Blue=focus, Amber=reminders, Purple=AI, Green=progress, Coral=urgency
const features = [
  {
    icon: Snowflake,
    name: "I'm Frozen",
    desc: "A 30-second restart for stuck moments.",
    span: "lg:col-span-2 lg:row-span-2",
    role: "amber-solid",
  },
  {
    icon: Leaf,
    name: "Clean Mode",
    desc: "Hide distracting page clutter instantly.",
    span: "lg:col-span-2",
    role: "green",
  },
  {
    icon: Cloud,
    name: "Parked Thoughts",
    desc: "Capture thoughts without chasing them.",
    span: "lg:col-span-2",
    role: "coral",
  },
  {
    icon: Brain,
    name: "Brain Dump",
    desc: "Unload messy thinking and see gentle patterns.",
    span: "lg:col-span-2",
    role: "purple",
  },
  {
    icon: Undo2,
    name: "Session Recovery",
    desc: "Return to the task, tab, and context you left.",
    span: "lg:col-span-2",
    role: "blue",
  },
  {
    icon: Target,
    name: "Just One Thing",
    desc: "Let Mindrail pick when deciding feels impossible.",
    span: "lg:col-span-3",
    role: "blue-solid",
  },
  {
    icon: Battery,
    name: "Energy Match",
    desc: "Find the right task size for your current energy.",
    span: "lg:col-span-3",
    role: "green",
  },
  {
    icon: Trophy,
    name: "Wins Journal",
    desc: "Keep proof of progress your brain might forget.",
    span: "lg:col-span-2",
    role: "amber",
  },
  {
    icon: Hourglass,
    name: "Visual Time Gravity",
    desc: "A subtle deadline bar without panic-based design.",
    span: "lg:col-span-2",
    role: "purple",
  },
  {
    icon: Users,
    name: "AI Body Double",
    desc: "A calm companion that works beside you, no social pressure.",
    span: "lg:col-span-2",
    role: "purple",
  },
];

const styleFor = (role) => {
  switch (role) {
    case "blue":
      return { bg: "#DBEAFE", icon: "#2563EB", text: "#0F172A", solid: false };
    case "blue-solid":
      return { bg: "#2563EB", icon: "#FFFFFF", text: "#FFFFFF", solid: true };
    case "amber":
      return { bg: "#FEF3C7", icon: "#F59E0B", text: "#0F172A", solid: false };
    case "amber-solid":
      return { bg: "#F59E0B", icon: "#FFFFFF", text: "#FFFFFF", solid: true };
    case "purple":
      return { bg: "#EDE9FE", icon: "#7C3AED", text: "#0F172A", solid: false };
    case "green":
      return { bg: "#DCFCE7", icon: "#22C55E", text: "#0F172A", solid: false };
    case "coral":
      return { bg: "#FFE4DE", icon: "#F9735B", text: "#0F172A", solid: false };
    default:
      return { bg: "#FFFFFF", icon: "#2563EB", text: "#0F172A", solid: false };
  }
};

export default function FeatureGrid() {
  return (
    <section
      id="features"
      data-testid="feature-grid-section"
      className="px-6 lg:px-10 py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        <div className="fade-up max-w-3xl">
          <p className="font-label text-sm tracking-[0.18em] uppercase font-semibold text-[#2563EB]">
            What&apos;s in the side panel
          </p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold leading-[1.05] text-[#0F172A]">
            Everything ADHD work needs, one side panel away.
          </h2>
          <p className="mt-4 text-lg text-[#475569] leading-relaxed max-w-2xl">
            Each feature uses the role its color is meant to carry — blue for
            focus, amber for gentle reminders, purple for AI support, green
            for calm progress, coral for soft urgency.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-5 auto-rows-[180px]">
          {features.map((f, i) => {
            const s = styleFor(f.role);
            return (
              <div
                key={f.name}
                data-testid={`feature-${f.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`fade-up rounded-3xl border p-6 lg:p-7 flex flex-col justify-between transition-all duration-500 hover:-translate-y-1 ${f.span}`}
                style={{
                  background: s.bg,
                  color: s.text,
                  borderColor: s.solid ? "transparent" : "#E2E8F0",
                  boxShadow: s.solid
                    ? "0 12px 32px rgba(15,23,42,0.08)"
                    : "0 4px 16px rgba(15,23,42,0.04)",
                  transitionDelay: `${i * 35}ms`,
                }}
              >
                <span
                  className="w-11 h-11 rounded-2xl flex items-center justify-center"
                  style={{
                    background: s.solid ? "rgba(255,255,255,0.18)" : "white",
                    border: s.solid
                      ? "1px solid rgba(255,255,255,0.28)"
                      : "1px solid #E2E8F0",
                  }}
                >
                  <f.icon
                    className="w-5 h-5"
                    strokeWidth={1.75}
                    style={{ color: s.icon }}
                  />
                </span>
                <div>
                  <h3 className="font-display text-xl lg:text-2xl tracking-tight font-semibold">
                    {f.name}
                  </h3>
                  <p
                    className="mt-2 text-sm leading-snug"
                    style={{
                      color: s.solid
                        ? "rgba(255,255,255,0.92)"
                        : "#475569",
                    }}
                  >
                    {f.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
